/**
 * Net Checkout PIN service -- the store editor's calls to the distributor-side
 * PIN endpoints (api/src/endpoints/campaign.py: GET/PUT/DELETE
 * /campaign/<id>/net-pin, and PUT /campaign/<id>/net-credential-mode for the
 * P1-1 choice between the store's own PIN and its Shared password).
 *
 * The PIN is write-only: it goes out in a PUT body and is never returned. GET
 * reports STATUS only ({pin_set, pin_set_at, pin_set_by, terms_version,
 * terms_current, terms_version_current, terms_text, terms_text_store_password,
 * store_password_terms_version, store_password_terms_accepted_at,
 * store_password_terms_current, ...}).
 *
 * Proposal: docs/planning/2026-10-23-net-checkout-pin-and-gate-decoupling-proposal.md
 */
import api from "./api";

// Mirror password_policy.MIN_NET_PIN_LENGTH / MAX_NET_PIN_LENGTH. The server
// stays the control; the editor uses these to hold a half-typed PIN back.
export const NET_PIN_MIN_LENGTH = 6;
export const NET_PIN_MAX_LENGTH = 64;
export const NET_PIN_SUGGESTED_LENGTH = 8;

// The terms version to acknowledge comes from GET /net-pin
// (terms_version_current); the client keeps no copy of it.

// The terms WORDS come from GET /net-pin too (terms_text for a PIN,
// terms_text_store_password for the store password), served from
// api/src/services/net_checkout_terms.py, where the version is derived from
// the words. The editor keeps no copy, so what it shows is always what the
// version it sends back names.

export const NET_CREDENTIAL_MODE_PIN = "pin";
export const NET_CREDENTIAL_MODE_STORE_PASSWORD = "store_password";

export async function getNetPinStatus(storeId) {
  const response = await api.get(`/campaign/${storeId}/net-pin`);
  return response.data;
}

export async function setNetPin(storeId, pin, termsVersion) {
  const response = await api.put(`/campaign/${storeId}/net-pin`, {
    pin,
    terms_version: termsVersion,
  });
  return response.data;
}

export async function clearNetPin(storeId) {
  const response = await api.delete(`/campaign/${storeId}/net-pin`);
  return response.data;
}

/**
 * Choose the store's Net credential (P1-1). ``termsVersion`` is the server's
 * current version and is required for "store_password" (that mode has no PIN
 * row to carry the acknowledgement); it is omitted for "pin". Answers with
 * the same status shape as getNetPinStatus.
 */
export async function setNetCredentialMode(storeId, mode, termsVersion) {
  const body = { mode };
  if (termsVersion !== undefined) body.terms_version = termsVersion;
  const response = await api.put(`/campaign/${storeId}/net-credential-mode`, body);
  return response.data;
}

/**
 * Owner-facing text for a failed call. 429 and 403 get fixed, status-aware
 * copy (their bodies are generic limiter/permission text); anything else
 * shows the server's own refusal, then the fallback. Never the request body
 * (it carries the PIN).
 */
export function netPinErrorMessage(err, fallback) {
  const status = err && err.response && err.response.status;
  if (status === 429) {
    return "Too many attempts. Wait a minute and try again.";
  }
  if (status === 403) {
    return "You do not have permission to change this store's Net Checkout PIN.";
  }
  const data = err && err.response && err.response.data;
  if (data && typeof data.error === "string" && data.error) return data.error;
  if (data && typeof data.message === "string" && data.message) {
    return data.message;
  }
  return fallback;
}
